import { useEffect } from "react";

import { Typography } from "@mui/material";

import { useWhiteScreen } from "../../../hooks/useWhiteScreen";
import { Container } from "../../common/Container/Container";

import "./privacy_policy.scss";

export function PrivacyPolicy() {
	useWhiteScreen();

	useEffect(() => {
		const hash = location.hash;
		if (hash) {
			const id = hash.slice(1);
			document.getElementById(id)?.scrollIntoView();
		}
	}, []);

	return (
		<Container
			className="privacy-policy-container"
			navbarOrkaProps={{ white: true }}>
			<Typography className="normal-title" textAlign="center">
				PRIVACY POLICY
			</Typography>

			<div className="section spacing-section">
				<div id="1-introduction" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#1-introduction">
					1. Introduction
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					This Privacy Policy describes how Fly ORKA, Inc. (hereinafter referred
					to as "we", "us", or "our") collects, uses, and shares information
					about you when you use{" "}
					<Typography
						component="a"
						href="https://www.flyorka.com"
						target="_blank">
						flyorka.com
					</Typography>
					, our mobile application, or any of our products or services. By using
					our services, you agree to the practices described in this Privacy
					Policy.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="2-information-we-collect"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#2-information-we-collect">
					2. Information We Collect
				</Typography>

				<div className="subsection">
					<div
						id="2-1-personal-information"
						className="section-scroll-position"
					/>
					<Typography
						className="subsection-title underline-on-hover-link"
						component="a"
						href="#2-1-personal-information">
						2.1. Personal Information
					</Typography>

					<Typography className="normal-text" textAlign="justify">
						We may collect personal information that you provide to us
						voluntarily, such as your name, email address, mailing address,
						phone number.
					</Typography>
				</div>

				<div className="subsection spacing-content">
					<div id="2-2-usage-information" className="section-scroll-position" />
					<Typography
						className="subsection-title underline-on-hover-link"
						component="a"
						href="#2-2-usage-information">
						2.2. Usage Information
					</Typography>
					<Typography className="normal-text" textAlign="justify">
						We may collect information about how you interact with our services,
						including the pages you visit, the links you click, and the features
						you use.
					</Typography>
				</div>

				<div className="subsection spacing-content">
					<div
						id="2-3-device-information"
						className="section-scroll-position"
					/>
					<Typography
						className="subsection-title underline-on-hover-link"
						component="a"
						href="#2-3-device-information">
						2.3. Device Information
					</Typography>
					<Typography className="normal-text" textAlign="justify">
						We may collect information about the devices you use to access our
						services, including the device type, operating system, unique device
						identifiers, and mobile network information.
					</Typography>
				</div>

				<div className="subsection spacing-content">
					<div
						id="2-4-location-information"
						className="section-scroll-position"
					/>
					<Typography
						className="subsection-title underline-on-hover-link"
						component="a"
						href="#2-4-location-information">
						2.4. Location Information
					</Typography>
					<Typography className="normal-text" textAlign="justify">
						We may collect precise or approximate location information through
						your mobile device when you use our services. This information helps
						us provide location-based services and improve your experience.
					</Typography>
					<Typography
						className="normal-text spacing-content"
						textAlign="justify">
						You may control your location data sharing through your device's
						settings, and you can disable location tracking at any time.
					</Typography>
					<Typography
						className="normal-text spacing-content"
						textAlign="justify">
						We collect location data in accordance with your preferences, and
						the data may include GPS coordinates, network-based location, and
						time stamps.
					</Typography>
				</div>
			</div>

			<div className="section spacing-section">
				<div id="3-tracking-technologies" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#3-tracking-technologies">
					3. Tracking Technologies
				</Typography>

				<div className="subsection">
					<div id="3-1-google-analytics" className="section-scroll-position" />
					<Typography
						className="subsection-title underline-on-hover-link"
						component="a"
						href="#3-1-google-analytics">
						3.1. Google Analytics
					</Typography>

					<Typography className="normal-text" textAlign="justify">
						We use Google Analytics, a web analytics service provided by Google,
						Inc. ("Google"). Google Analytics uses cookies to help us analyze
						how users use our website. The information generated by the cookie
						about your use of our website (including your IP address) will be
						transmitted to and stored by Google on servers in the United States.
						Google will use this information on our behalf for the purpose of
						evaluating your use of our website, compiling reports on website
						activity, and providing other services relating to website activity
						and internet usage.
					</Typography>

					<Typography
						className="normal-text spacing-content"
						textAlign="justify">
						Google may also transfer this information to third parties where
						required to do so by law, or where such third parties process the
						information on Google's behalf. Google will not associate your IP
						address with any other data held by Google. You may refuse the use
						of cookies by selecting the appropriate settings on your browser,
						however please note that if you do this you may not be able to use
						the full functionality of our website.
					</Typography>

					<Typography
						className="normal-text spacing-content"
						textAlign="justify">
						By using our website, you consent to the processing of data about
						you by Google in the manner and for the purposes set out above. For
						more information, see Google's Privacy Policy.
					</Typography>
				</div>
			</div>

			<div className="section spacing-section">
				<div
					id="4-your-public-contributions"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#4-your-public-contributions">
					4. Your Public Contributions
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Our services are designed to support your success by fostering
					community engagement and enabling flight visualization. As part of
					these services, we share your public data at your direction.
				</Typography>

				<Typography className="normal-text spacing-content" textAlign="justify">
					When you use our flight recording feature, you create a permanent,
					publicly accessible record of your location history. This data may be
					viewed and utilized by others unless you modify your flight visibility
					settings. If you prefer not to share your flights, you may adjust your
					flight visibility settings at any time. Additionally, you retain the
					ability to delete your recorded flights from our application.
				</Typography>

				<Typography className="normal-text spacing-content" textAlign="justify">
					Please be aware that any data you choose to make public, as well as
					aggregated public data disclosed by us, may be analyzed and used by
					third parties to derive insights about users.
				</Typography>

				<Typography className="normal-text spacing-content" textAlign="justify">
					We may use your public information, either in aggregate with others'
					public data or individually, to develop new features, enhance our
					services, or create data-related products that benefit our users.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="5-how-we-use-your-information"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#5-how-we-use-your-information">
					5. How We Use Your Information
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					We may use the information we collect for various purposes, including:
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- To provide, maintain, and improve our services
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- To communicate with you and respond to your inquiries
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- To personalize your experience and deliver relevant content and
					advertisements
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- To analyze and understand how our services are used
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- To detect and prevent fraud, abuse, and other illegal activities
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="6-information-sharing-and-disclosure"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#6-information-sharing-and-disclosure">
					6. Information Sharing and Disclosure
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					We may share your information in the following circumstances:
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- With third-party service providers who assist us in operating our
					services
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- To comply with legal obligations or protect our rights and interests
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- In the event of a merger, acquisition, or other corporate
					restructuring
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					- With your consent or at your direction
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="7-google-apis-limited-use-disclosure"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#7-google-apis-limited-use-disclosure">
					7. Google APIs Limited Use Disclosure
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Use and transfer to any other app of information received from Google
					APIs will adhere to the Google API Services User Data Policy,
					including the Limited Use requirements.
				</Typography>
				<Typography className="normal-text" textAlign="justify">
					See{" "}
					<Typography
						href="https://developers.google.com/terms/api-services-user-data-policy"
						component="a"
						target="_blank">
						Google API Services User Data Policy
					</Typography>{" "}
					for more information.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="8-data-security" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#8-data-security">
					8. Data Security
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					We implement appropriate technical and organizational measures to
					protect your information from unauthorized access, disclosure, or
					destruction.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="9-your-rights-and-choices"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#9-your-rights-and-choices">
					9. Your Rights and Choices
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					You may have certain rights and choices regarding your personal
					information, such as the right to access, update, or delete your
					information, or to opt-out of certain data processing activities.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="10-children-privacy" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#10-children-privacy">
					10. Children's Privacy
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					Our services are not directed towards children under the age of 13. We
					do not knowingly collect personal information from children under 13.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div
					id="11-updates-to-this-privacy-policy"
					className="section-scroll-position"
				/>
				<Typography
					className="section-title"
					component="a"
					href="#11-updates-to-this-privacy-policy">
					11. Updates to This Privacy Policy
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					We may update this Privacy Policy from time to time. We will notify
					you of any material changes by posting the updated policy on our
					website or by other appropriate means.
				</Typography>
			</div>

			<div className="section spacing-section">
				<div id="12-contact-us" className="section-scroll-position" />
				<Typography
					className="section-title"
					component="a"
					href="#12-contact-us">
					12. Contact Us
				</Typography>

				<Typography className="normal-text" textAlign="justify">
					If you have any questions or concerns about this Privacy Policy or our
					data practices, please contact us at{" "}
					<Typography
						component="a"
						href="mailto:admin@flyorka.com"
						target="_blank">
						admin@flyorka.com
					</Typography>
					.
				</Typography>
			</div>

			<div className="section spacing-section">
				<Typography className="normal-text" textAlign="justify">
					Effective Date: 2024.01.30
				</Typography>
			</div>
		</Container>
	);
}
